import { Group, Portal, Stack, Text, useMantineTheme } from '@mantine/core';
import { ResponsiveLine, type SliceTooltipProps } from '@nivo/line';
import { useProfile } from 'init/use-enforce-profile';
import { useTranslation } from 'locales';
import { i18nKeys } from 'locales/source/i18nKeys';
import { upperFirst } from 'lodash-es';
import { entries, isNonNull, isNullish, map, pipe, zip } from 'remeda';
import { BarChartTooltip } from 'shared/charts';
import type { Cei } from 'shared/hooks/use-load-cei';

import { LegendSwatch } from './LegendSwatch';

const CURRENT_YEAR_SERIES_ID = 'cei';
const PREVIOUS_YEAR_SERIES_ID = 'cei_prev';
const NO_VALUE = '—';
const Y_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

interface CeiChartProps {
  cei: Cei;
}

export const CeiChart = ({ cei }: CeiChartProps) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useMantineTheme();

  const previousYear = cei.previous_year;
  const hasPreviousYear = Object.values(previousYear).some(isNonNull);

  const legendEntries = [
    ...(hasPreviousYear
      ? [
          {
            seriesId: PREVIOUS_YEAR_SERIES_ID,
            color: theme.colors.coral[4],
            label: t(i18nKeys.ANALYTICS.PREVIOUS_YEAR),
          },
        ]
      : []),
    {
      seriesId: CURRENT_YEAR_SERIES_ID,
      color: theme.colors.purple[6],
      label: t(i18nKeys.ANALYTICS.CURRENT_YEAR),
    },
  ];

  const hoverEntries = [...legendEntries].reverse();

  const ceiSliceTooltip = ({ slice }: SliceTooltipProps<any>) => {
    setTimeout(() => {
      const tooltip = document.getElementById('tooltip-fix');
      if (tooltip) tooltip.style.opacity = '1';
    }, 50);

    const currentYearPoint = slice.points.find(p => p.seriesId === CURRENT_YEAR_SERIES_ID);

    if (isNullish(currentYearPoint)) return null;

    return (
      <BarChartTooltip style={{ opacity: 0, transition: 'all 0.1s ease-in-out' }} id="tooltip-fix">
        <Stack gap="space3">
          <Text fw={700}>
            {upperFirst(
              (currentYearPoint.data.x as Date).toLocaleDateString(profile.locale, {
                month: 'long',
              }),
            )}
          </Text>
          <Stack
            component="ul"
            gap="space2"
            style={{ margin: 0, padding: 0, listStyleType: 'none' }}
          >
            {hoverEntries.map(({ seriesId, color, label }) => {
              const point = slice.points.find(candidate => candidate.seriesId === seriesId);

              return (
                <Group key={seriesId} component="li" aria-label={label} gap="space3" wrap="nowrap">
                  <LegendSwatch color={color} />
                  <Text fw={700} style={{ whiteSpace: 'nowrap' }}>
                    {isNullish(point?.data.y) ? NO_VALUE : `${point.data.y} %`}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        </Stack>
      </BarChartTooltip>
    );
  };

  const previousYearSeries = hasPreviousYear
    ? [
        {
          id: PREVIOUS_YEAR_SERIES_ID,
          color: theme.colors.coral[3],
          pointColor: theme.colors.coral[4],
          data: pipe(
            entries(cei.current_year),
            zip(entries(previousYear)),
            map(([[currDate], [, prevValue]]) => ({ x: new Date(currDate), y: prevValue })),
          ),
        },
      ]
    : [];

  return (
    <>
      <ResponsiveLine
        isInteractive
        enableSlices="x"
        enableArea
        defs={[
          {
            colors: [
              {
                color: 'inherit',
                offset: 0,
              },
              {
                color: 'inherit',
                offset: 100,
                opacity: 0,
              },
            ],
            id: 'AreaGradient',
            type: 'linearGradient',
          },
        ]}
        fill={[
          {
            id: 'AreaGradient',
            match: '*',
          },
        ]}
        useMesh
        curve="monotoneX"
        enableCrosshair
        margin={{ top: 10, right: 15, bottom: 35, left: 55 }}
        axisBottom={{
          tickPadding: 15,
          tickSize: 0,
          format: (date: Date) => date.toLocaleDateString(profile.locale, { month: 'short' }),
        }}
        axisLeft={{
          tickPadding: 15,
          tickValues: Y_STEPS,
          tickSize: 0,
          format: (value: number) => `${value} %`,
        }}
        sliceTooltip={ceiSliceTooltip}
        yScale={{ type: 'linear', min: 0, max: 100 }}
        gridYValues={Y_STEPS}
        enableGridX={false}
        pointSize={8}
        pointBorderWidth={2}
        colors={{ datum: 'color' }}
        pointColor={({ series }) => series.pointColor}
        theme={{
          grid: {
            line: {
              stroke: theme.colors.gray[3],
              strokeDasharray: 7,
              strokeDashoffset: 15,
            },
          },
          labels: {
            text: {
              fontSize: 14,
              fill: theme.colors.gray[6],
            },
          },
          text: {
            fontSize: 12,
            fill: theme.colors.gray[6],
          },
        }}
        data={[
          {
            id: CURRENT_YEAR_SERIES_ID,
            color: theme.colors.purple[4],
            pointColor: theme.colors.purple[6],
            data: Object.entries(cei.current_year).map(([key, value]) => ({
              x: new Date(key),
              y: value,
            })),
          },
          ...previousYearSeries,
        ]}
      />
      {/* Because of how ResponsiveLine grows, putting an element directly underneath it causes that element to overlay the chart*/}
      <Portal target="#cei-legend-outlet">
        <Group
          component="ul"
          w="100%"
          gap="space10"
          justify="center"
          mt="space5"
          style={{ padding: 0, listStyleType: 'none' }}
        >
          {legendEntries.map(({ seriesId, color, label }) => (
            <Group key={seriesId} component="li" gap="space3">
              <LegendSwatch color={color} />
              <Text size="xs">{label}</Text>
            </Group>
          ))}
        </Group>
      </Portal>
    </>
  );
};
